import { useRef, useState, useEffect } from 'react'
import { getConnectivitySnapshot, subscribeConnectivity } from '../lib/connectivity'

export interface UseAutoRefreshOptions {
  baseInterval: number    // Starting poll interval in ms (e.g., 5000, 30000)
  maxInterval: number     // Maximum backoff interval in ms (e.g., 60000, 300000)
  enabled?: boolean       // Whether polling is active (default: true)
}

export interface UseAutoRefreshReturn {
  isBackingOff: boolean       // True when current interval > baseInterval
  currentInterval: number     // Current polling interval in ms
  lastErrorMessage: string | null
  retryAfterSeconds: number | null
  isPaused: boolean           // True while polling is suspended for being offline
}

function getRetryAfterIntervalMs(
  error: unknown,
  baseInterval: number,
  maxInterval: number
): number | null {
  if (!error || typeof error !== 'object') {
    return null
  }

  const retryAfterSeconds = (error as {
    retryAfterSeconds?: unknown
    details?: { retry_after_seconds?: unknown }
  }).retryAfterSeconds
    ?? (error as { details?: { retry_after_seconds?: unknown } }).details?.retry_after_seconds

  if (typeof retryAfterSeconds !== 'number' || !Number.isFinite(retryAfterSeconds) || retryAfterSeconds < 0) {
    return null
  }

  return Math.min(Math.max(Math.ceil(retryAfterSeconds * 1000), baseInterval), maxInterval)
}

export function useAutoRefresh(
  fetchFn: () => Promise<void>,
  options: UseAutoRefreshOptions
): UseAutoRefreshReturn {
  const { baseInterval, maxInterval, enabled = true } = options

  // Store fetchFn in a ref to avoid stale closures and prevent
  // the polling cycle from restarting when the caller recreates fetchFn
  const fetchFnRef = useRef(fetchFn)
  fetchFnRef.current = fetchFn

  // Expose isBackingOff and currentInterval via useState for UI consumption
  const [displayInterval, setDisplayInterval] = useState(baseInterval)
  const [lastErrorMessage, setLastErrorMessage] = useState<string | null>(null)
  const [retryAfterSeconds, setRetryAfterSeconds] = useState<number | null>(null)
  const [isPaused, setIsPaused] = useState(false)

  useEffect(() => {
    if (!enabled) {
      setDisplayInterval(baseInterval)
      setLastErrorMessage(null)
      setRetryAfterSeconds(null)
      setIsPaused(false)
      return
    }

    // Per-invocation cancellation flag. This avoids the race condition where
    // a shared ref (mountedRef) gets set back to true by a new effect run
    // while an in-flight fetch from the previous invocation is still pending.
    let active = true
    let currentInterval = baseInterval
    let timeoutId: ReturnType<typeof setTimeout> | null = null

    function schedulePoll(delay = currentInterval) {
      timeoutId = setTimeout(async () => {
        // Polling while offline just burns battery to collect failures, and
        // — worse — each failure doubles the interval, so a two-minute tunnel
        // would leave the page stale for another five minutes after signal
        // returns. Hold position instead; the reconnect handler below fires an
        // immediate poll the moment the network is back.
        if (getConnectivitySnapshot().status === 'offline') {
          if (active) {
            setIsPaused(true)
            schedulePoll(baseInterval)
          }
          return
        }

        if (active) setIsPaused(false)

        try {
          await fetchFnRef.current()
          // On success: reset to base interval
          currentInterval = baseInterval
          if (active) {
            setLastErrorMessage(null)
            setRetryAfterSeconds(null)
          }
        } catch (error) {
          const retryAfterInterval = getRetryAfterIntervalMs(error, baseInterval, maxInterval)
          currentInterval = retryAfterInterval ?? Math.min(currentInterval * 2, maxInterval)
          const message = error instanceof Error
            ? error.message
            : typeof error === 'string'
              ? error
              : 'Auto-refresh failed'
          const retryAfter = (
            error
            && typeof error === 'object'
            && typeof (error as { retryAfterSeconds?: unknown }).retryAfterSeconds === 'number'
            && Number.isFinite((error as { retryAfterSeconds?: number }).retryAfterSeconds)
            && (error as { retryAfterSeconds?: number }).retryAfterSeconds! >= 0
          )
            ? (error as { retryAfterSeconds: number }).retryAfterSeconds
            : (
                error
                && typeof error === 'object'
                && typeof (error as { details?: { retry_after_seconds?: unknown } }).details?.retry_after_seconds === 'number'
                && Number.isFinite((error as { details?: { retry_after_seconds?: number } }).details?.retry_after_seconds)
                && (error as { details?: { retry_after_seconds?: number } }).details!.retry_after_seconds! >= 0
              )
              ? (error as { details: { retry_after_seconds: number } }).details.retry_after_seconds
              : Math.ceil(currentInterval / 1000)
          if (active) {
            setLastErrorMessage(message)
            setRetryAfterSeconds(retryAfter)
          }
        }

        if (active) {
          setDisplayInterval(currentInterval)
          // Schedule the next poll after this one completes (poll-after-response)
          schedulePoll()
        }
      }, delay)
    }

    // Start polling
    setDisplayInterval(baseInterval)
    setIsPaused(false)
    schedulePoll()

    // Reconnecting is the one moment the user is most likely to be staring at
    // stale data, so skip the remaining wait and refresh straight away.
    let wasOffline = getConnectivitySnapshot().status === 'offline'
    const unsubscribe = subscribeConnectivity(() => {
      const nowOffline = getConnectivitySnapshot().status === 'offline'
      if (wasOffline && !nowOffline && active) {
        currentInterval = baseInterval
        setDisplayInterval(baseInterval)
        if (timeoutId !== null) clearTimeout(timeoutId)
        schedulePoll(0)
      }
      wasOffline = nowOffline
    })

    return () => {
      active = false
      unsubscribe()
      if (timeoutId !== null) {
        clearTimeout(timeoutId)
        timeoutId = null
      }
    }
  }, [baseInterval, maxInterval, enabled])

  return {
    isBackingOff: displayInterval > baseInterval,
    currentInterval: displayInterval,
    lastErrorMessage,
    retryAfterSeconds,
    isPaused,
  }
}
